import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Platform } from '@ionic/angular/standalone';
import { NavigationError, Router } from '@angular/router';
import { Feature, LoggerService } from './logger.service';
import { WebAppUpdateAvailableModalPage } from '../modals/web-app-update-available/web-app-update-available-modal.page';
import { AppState } from '../store/app.state';
import { runtimeEnvironment } from '../../environments/environment.r';
import { ComponentRef } from '@ionic/core';
import { ModalService } from './modal.service';

export enum PlatformType {
  IOS = 'Ios',
  Android = 'android',
  WebDesktop = 'webDesktop',
  WebMobile = 'webMobile',
}

@Injectable({ providedIn: 'root' })
export class AppService {
  isMobile$!: BehaviorSubject<boolean>;
  isTablet$!: BehaviorSubject<boolean>;

  readonly platformType: PlatformType = this.getPlatform();

  private readonly mobileResolution = 768;
  private readonly desktopResolution = 1280;

  constructor(
    private platform: Platform,
    private router: Router,
    private modalService: ModalService,
    private loggerService: LoggerService,
    private appState: AppState,
  ) {}

  initialize(): void {
    this.updateIsMobileView(window.innerWidth);
    void this.listenToAppEvents();
  }

  updateIsMobileView(innerWidth: number): void {
    const isMobile = innerWidth < this.mobileResolution;
    const isTablet = innerWidth <= this.desktopResolution && innerWidth >= this.mobileResolution;

    if (this.isMobile$) {
      this.isMobile$.next(isMobile);
      this.isTablet$.next(isTablet);
    } else {
      this.isMobile$ = new BehaviorSubject<boolean>(isMobile);
      this.isTablet$ = new BehaviorSubject<boolean>(isTablet);
    }
  }

  async listenToAppEvents(): Promise<void> {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationError) {
        const error = event.error;
        if (error.message.includes('Failed to fetch dynamically imported module') || error.message.includes('Importing a module script failed')) {
          void this.updateApp(event.url ?? '/home');
        }
      }
    });
  }

  private getPlatform(): PlatformType {
    if (this.platform.is('desktop')) {
      return PlatformType.WebDesktop;
    } else if (this.platform.is('mobileweb')) {
      return PlatformType.WebMobile;
    } else if (this.platform.is('ios')) {
      return PlatformType.IOS;
    } else {
      return PlatformType.Android;
    }
  }

  async updateApp(url: string): Promise<void> {
    // set build number in localstorage
    if (this.appState.app$.value.lastAppUpdateBuildNumber === runtimeEnvironment.BUILD_NUMBER) {
      this.loggerService.error('AppService::updateApp', {
        feature: Feature.APP_INIT,
        context: 'Could not persistent fetch dynamically imported module after app reload',
      });
      return;
    }

    try {
      const categoriesModal = await this.modalService.create({
        componentProps: { url },
        component: WebAppUpdateAvailableModalPage as ComponentRef,
        cssClass: 'wr-modal web-app-update',
        canDismiss: false,
        backdropDismiss: false,
      });

      categoriesModal && (await categoriesModal.present());
    } catch {
      await this.appState.set({ lastAppUpdateBuildNumber: runtimeEnvironment.BUILD_NUMBER });
      window.location.href = url;
      this.loggerService.error('AppService::updateApp', {
        feature: Feature.APP_INIT,
        context: 'Could not display WebAppUpdateAvailableModalComponent modal',
      });
    }
  }
}
